import React, { useState } from 'react';
import {
  TrendingUp,
  Search,
  CheckCircle2,
  AlertCircle,
  Globe,
  Code,
  ShieldCheck,
  FileCode,
  ListFilter,
  Sparkles,
  BarChart3,
  ExternalLink,
  Zap,
} from 'lucide-react';
import { seoCmsEngine } from '../../services/seoCmsEngine';

export const OrganicGrowthCenter: React.FC = () => {
  const summary = seoCmsEngine.getSearchPerformanceSummary();
  const searchObservations = seoCmsEngine.getSearchObservations();
  const opportunities = seoCmsEngine.getOpportunities();
  const programmaticPages = seoCmsEngine.getProgrammaticPages();
  const sitemapEntries = seoCmsEngine.generateSitemapEntries();

  const [activeTab, setActiveTab] = useState<'overview' | 'programmatic' | 'opportunities' | 'sitemaps'>('overview');
  const [showSitemapModal, setShowSitemapModal] = useState(false);

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <div className="bg-slate-900 text-white rounded-2xl p-8 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="space-y-2 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold border border-emerald-500/30">
            <TrendingUp className="w-3.5 h-3.5" />
            YamooPay Organic Growth & Search Console Command Center
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight">
            Search Performance & Indexability Governance
          </h1>
          <p className="text-slate-300 text-sm leading-relaxed">
            Monitor real-time Google Search Console performance, quality-gated programmatic SEO inventories, reciprocal hreflang graphs, and XML sitemap health.
          </p>
        </div>

        <button
          onClick={() => setShowSitemapModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold rounded-xl transition-all shadow-md shrink-0"
        >
          <FileCode className="w-4 h-4" />
          View sitemap.xml Index
        </button>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Organic Clicks</span>
            <BarChart3 className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-3xl font-black text-slate-900">{summary.totalClicks.toLocaleString()}</div>
          <div className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
            ↑ +18.4% vs previous period
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Impressions</span>
            <Search className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-3xl font-black text-slate-900">{summary.totalImpressions.toLocaleString()}</div>
          <div className="text-xs text-slate-500">Average CTR: <span className="font-bold text-slate-800">{summary.avgCtr}%</span></div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Average Position</span>
            <TrendingUp className="w-4 h-4 text-indigo-600" />
          </div>
          <div className="text-3xl font-black text-slate-900">{summary.avgPosition}</div>
          <div className="text-xs text-indigo-600 font-semibold">Top 3 across major intent pairs</div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Indexability Gate</span>
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-3xl font-black text-slate-900">{summary.indexedCount} <span className="text-sm font-semibold text-slate-400">Indexed</span></div>
          <div className="text-xs text-amber-600 font-semibold">
            {summary.noindexCount} Quality-Gated Noindex
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
            activeTab === 'overview'
              ? 'bg-emerald-600 text-white shadow-sm'
              : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
          }`}
        >
          Search Console Performance
        </button>
        <button
          onClick={() => setActiveTab('programmatic')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
            activeTab === 'programmatic'
              ? 'bg-emerald-600 text-white shadow-sm'
              : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
          }`}
        >
          Programmatic SEO Quality Gate
        </button>
        <button
          onClick={() => setActiveTab('opportunities')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
            activeTab === 'opportunities'
              ? 'bg-emerald-600 text-white shadow-sm'
              : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
          }`}
        >
          Query Opportunity Engine ({summary.openOpportunitiesCount})
        </button>
      </div>

      {/* Tab 1: Search Console Observations */}
      {activeTab === 'overview' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div>
              <h2 className="font-bold text-base text-slate-900">Recent Search Query Performance Observations</h2>
              <p className="text-xs text-slate-500">Idempotent daily snapshots imported via Google Search Console API</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider font-bold border-b border-slate-200">
                <tr>
                  <th className="p-3">Target Query</th>
                  <th className="p-3">Landing Page URL</th>
                  <th className="p-3">Country</th>
                  <th className="p-3">Device</th>
                  <th className="p-3">Clicks</th>
                  <th className="p-3">Impressions</th>
                  <th className="p-3">CTR</th>
                  <th className="p-3">Avg Position</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-800 font-medium">
                {searchObservations.map((obs) => (
                  <tr key={obs.id} className="hover:bg-slate-50/80">
                    <td className="p-3 font-bold text-slate-900">{obs.query}</td>
                    <td className="p-3 font-mono text-[11px] text-emerald-700 max-w-xs truncate">{obs.pageUrl}</td>
                    <td className="p-3 font-semibold">{obs.country}</td>
                    <td className="p-3 text-slate-500">{obs.device}</td>
                    <td className="p-3 font-bold text-slate-900">{obs.clicks.toLocaleString()}</td>
                    <td className="p-3 text-slate-600">{obs.impressions.toLocaleString()}</td>
                    <td className="p-3 text-emerald-700 font-bold">{(obs.ctr * 100).toFixed(1)}%</td>
                    <td className="p-3 font-bold text-indigo-700">{obs.position}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab 2: Programmatic Quality Gate */}
      {activeTab === 'programmatic' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div>
              <h2 className="font-bold text-base text-slate-900">Programmatic Page Inventory & Quality Gate</h2>
              <p className="text-xs text-slate-500">
                Evaluates DataReadiness & Uniqueness before granting indexability to prevent Scaled Content Abuse.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {programmaticPages.map((page) => {
              const evalResult = seoCmsEngine.evaluateIndexability(page);

              return (
                <div
                  key={page.id}
                  className="p-5 rounded-xl border border-slate-200 bg-slate-50/50 space-y-3"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/60 pb-2">
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2.5 py-0.5 rounded text-xs font-bold uppercase ${
                          page.indexabilityStatus === 'INDEXABLE'
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {page.indexabilityStatus}
                      </span>
                      <span className="font-mono text-xs text-slate-700 font-bold">{page.url}</span>
                    </div>

                    <div className="flex items-center gap-2 text-xs font-semibold">
                      <span className="text-slate-500">Locale: {page.locale}</span>
                      <span className="text-slate-400">•</span>
                      <span className="text-slate-500">Market: {page.market}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                    <div className="bg-white p-3 rounded-lg border border-slate-200">
                      <div className="text-slate-400 font-bold uppercase text-[10px]">Data Readiness Score</div>
                      <div className="text-lg font-black text-slate-900">{page.dataReadinessScore} / 100</div>
                    </div>

                    <div className="bg-white p-3 rounded-lg border border-slate-200">
                      <div className="text-slate-400 font-bold uppercase text-[10px]">Uniqueness Score</div>
                      <div className="text-lg font-black text-slate-900">{page.uniquenessScore} / 100</div>
                    </div>

                    <div className="bg-white p-3 rounded-lg border border-slate-200">
                      <div className="text-slate-400 font-bold uppercase text-[10px]">Hreflang Alternates</div>
                      <div className="text-xs font-bold text-emerald-700">
                        {Object.keys(page.hreflangGroup).length} Regional Variants
                      </div>
                    </div>
                  </div>

                  {page.noindexReason && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <span className="font-bold">Quality Gate Decision: </span>
                        {page.noindexReason}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tab 3: Opportunities */}
      {activeTab === 'opportunities' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div>
              <h2 className="font-bold text-base text-slate-900">Query Opportunity Engine</h2>
              <p className="text-xs text-slate-500">Identifies High Impression / Low CTR queries and Position 4–15 quick wins</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {opportunities.map((opp) => (
              <div key={opp.id} className="p-5 rounded-xl border border-slate-200 bg-white space-y-3 shadow-xs">
                <div className="flex items-center justify-between">
                  <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold text-xs">
                    Opportunity Score: {opp.opportunityScore}/100
                  </span>
                  <span className="text-xs font-bold text-slate-500">{opp.recommendationType}</span>
                </div>

                <h3 className="font-bold text-slate-900 text-sm">{opp.title}</h3>

                <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-xs space-y-1">
                  <div>
                    <span className="text-slate-500">Target Query: </span>
                    <span className="font-bold text-slate-900">{opp.targetQuery}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Current Position: </span>
                    <span className="font-bold text-indigo-700">{opp.currentPosition}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Est. Monthly Click Gain: </span>
                    <span className="font-bold text-emerald-700">+{opp.potentialClicks} clicks</span>
                  </div>
                </div>

                <button className="w-full py-2 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800 transition-colors">
                  Apply Title & Schema Recommendation
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sitemap Index Modal */}
      {showSitemapModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 text-slate-100 rounded-2xl w-full max-w-3xl border border-slate-800 shadow-2xl p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <FileCode className="w-5 h-5 text-emerald-400" />
                <h3 className="font-bold text-base text-white">sitemap.xml Index & Canonical Registry</h3>
              </div>
              <button
                onClick={() => setShowSitemapModal(false)}
                className="text-slate-400 hover:text-white text-xs font-bold px-2 py-1 rounded bg-slate-800"
              >
                Close
              </button>
            </div>

            <p className="text-xs text-slate-400">
              Segmented XML sitemap containing only canonical, indexability-verified URLs with accurate lastmod timestamps.
            </p>

            <pre className="bg-slate-950 p-4 rounded-xl text-xs font-mono text-emerald-300 overflow-x-auto max-h-96 border border-slate-800 leading-relaxed">
              {`<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"\n        xmlns:xhtml="http://www.w3.org/1999/xhtml">\n` +
                sitemapEntries
                  .map(
                    (e) =>
                      `  <url>\n    <loc>${e.loc}</loc>\n    <lastmod>${e.lastmod}</lastmod>\n    <changefreq>${e.changefreq}</changefreq>\n    <priority>${e.priority}</priority>\n` +
                      e.alternateHreflangs
                        .map((h) => `    <xhtml:link rel="alternate" hreflang="${h.lang}" href="${h.href}" />\n`)
                        .join('') +
                      `  </url>`
                  )
                  .join('\n') +
                `\n</urlset>`}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};
